import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  Stack,
  Container,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle,
  Close as CloseIcon,
  Iron,
  LocalLaundryService,
  Inventory2,
} from '@mui/icons-material';
import SectionBlock from '../components/SectionBlock';
import PageLayout from '../components/PageLayout';
import Seo from '../components/Seo';
import { getBookingFlowPath } from '../utils/featureFlags';
import LinenCalculator from '../components/linen/LinenCalculator';
import LinenProductGallery from '../components/linen/LinenProductGallery';
import linenProducts from '../data/linenProducts.json';

const programIncludes = [
  'Three complete linen rotations',
  'Hotel-quality linens and towels',
  'Inventory management',
  'Quality inspections',
  'Replacement due to normal wear',
  'Inventory replenishment',
  'Storage management',
  'Capital investment in the linen inventory',
];

const turnoverIncludes = [
  'Bed stripping',
  'On-site washing and drying',
  'Bed making',
  'Towel presentation',
  'Inventory rotation',
  'Damage inspection',
  'Quality assurance',
];

const setupComparison = [
  ['Initial investment in hospitality linens', '$1,200', '$0'],
  ['Three complete linen rotations', 'Owner purchases', 'Included'],
  ['Hospitality-grade towels', 'Owner purchases', 'Included'],
  ['Mattress & pillow protectors', 'Owner purchases', 'Included'],
  ['Linen inventory management', 'Owner responsibility', 'Included'],
  ['Replacement due to normal wear', 'Owner responsibility', 'Included'],
  ['Matching replacement purchases', 'Owner responsibility', 'Included'],
  ['Inventory inspections', 'Owner responsibility', 'Included'],
  ['Hotel-quality linen standards', 'Owner responsibility', 'Included'],
  ['Upfront capital required', '$1,200', 'None'],
];

const withoutProgram = [
  'Invest approximately $1,200 upfront.',
  'Purchase replacement sheets and towels throughout the year.',
  'Manage inventory and replacements yourself.',
  'Find matching products when items are discontinued.',
  'Store and organize backup linen inventory.',
];

const withProgram = [
  'No upfront investment',
  'Professionally managed hospitality-grade linen inventory',
  'Three complete linen rotations always available',
  'Replacement due to normal wear included',
  'Consistent hotel-quality presentation',
  'Predictable monthly operating cost',
  'No inventory management or replacement sourcing',
  'Fully integrated with your scheduled turnover cleaning',
];

const HospitalityLinenPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <PageLayout>
      <Seo
        title="Hospitality Linen Program for Vacation Rentals"
        description="Hotel-quality linens for your short-term rental without the upfront investment. Compare owning your own linens with our Hospitality Linen Program and turnover linen service."
        path="/hospitality-linen"
      />

      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: { xs: 6, md: 9 },
          px: 2,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Chip label="Short-Term Rentals" color="primary" sx={{ mb: 2, fontWeight: 700 }} />
          <Typography variant="h2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2.1rem', md: '3rem' } }}>
            Hospitality Linen Program
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 400 }}>
            A complete hospitality linen management solution that provides professionally managed
            hotel-quality linens, ongoing inventory replacement, and seamless integration with
            every vacation rental turnover.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" href="#linen-calculator">
              Calculate Your Cost
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* The two services */}
      <SectionBlock
        title="Two Services, One Seamless Experience"
        subtitle="The monthly program keeps a hotel-quality inventory ready for your property; the turnover service handles the linens at every scheduled cleaning."
        maxWidth="lg"
      >
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderTop: `4px solid ${theme.palette.primary.main}` }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                  <Inventory2 color="primary" sx={{ fontSize: 34 }} />
                  <Typography variant="h5" fontWeight={700}>Hospitality Linen Program</Typography>
                </Stack>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  The monthly program provides access to a professionally managed
                  hospitality-grade linen inventory. It covers:
                </Typography>
                <List dense>
                  {programIncludes.map((item) => (
                    <ListItem key={item} disableGutters>
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderTop: `4px solid ${theme.palette.secondary.dark}` }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                  <LocalLaundryService sx={{ fontSize: 34, color: 'secondary.dark' }} />
                  <Typography variant="h5" fontWeight={700}>Turnover Linen Service</Typography>
                </Stack>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Performed during your scheduled turnover cleaning. Includes:
                </Typography>
                <List dense>
                  {turnoverIncludes.map((item) => (
                    <ListItem key={item} disableGutters>
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SectionBlock>

      {/* The actual linen inventory */}
      <SectionBlock
        title={linenProducts.title}
        subtitle={linenProducts.subtitle}
        maxWidth="lg"
      >
        <LinenProductGallery />
      </SectionBlock>

      {/* Calculator */}
      <Box id="linen-calculator">
        <SectionBlock
          title="What Would It Cost for Your Property?"
          subtitle="Build your property's linen profile and get instant program pricing, turnover fees, and monthly and annual estimates."
          bgcolor={alpha(theme.palette.primary.main, 0.04)}
          maxWidth="lg"
        >
          <LinenCalculator />
        </SectionBlock>
      </Box>

      {/* Worked example comparison */}
      <SectionBlock
        title="Hospitality Linen Program vs. Managing Your Own Linens"
        subtitle="Example: 2 Bedroom / 2 Bathroom vacation rental (3 beds), 30 turnovers per year, three complete linen rotations, hospitality-grade linens and towels."
        maxWidth="lg"
      >
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                <TableCell sx={{ fontWeight: 700 }}>Setup</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Owner Manages Linens</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>5 Fairies Hospitality Linen Program</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {setupComparison.map(([label, owner, program]) => (
                <TableRow key={label}>
                  <TableCell>{label}</TableCell>
                  <TableCell>{owner}</TableCell>
                  <TableCell sx={{ color: program === 'Included' || program === '$0' || program === 'None' ? 'success.main' : 'inherit', fontWeight: 600 }}>
                    {program}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </SectionBlock>

      {/* What you gain */}
      <SectionBlock
        title="What You Gain"
        bgcolor={alpha(theme.palette.primary.main, 0.04)}
        maxWidth="lg"
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 4, height: '100%', borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Without the Hospitality Linen Program
              </Typography>
              <List dense>
                {withoutProgram.map((item) => (
                  <ListItem key={item} disableGutters>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <CloseIcon color="disabled" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 3,
                border: `2px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography variant="h6" fontWeight={700} gutterBottom color="primary">
                With the Hospitality Linen Program
              </Typography>
              <List dense>
                {withProgram.map((item) => (
                  <ListItem key={item} disableGutters>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </SectionBlock>

      {/* CTA */}
      <SectionBlock maxWidth="md">
        <Box textAlign="center">
          <Iron color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Hotel-quality linens. Zero linen headaches.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Join the Hospitality Linen Program and let every turnover come with crisp,
            professionally managed linens — no upfront investment, no inventory chores.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" onClick={() => navigate(getBookingFlowPath())}>
              Explore Turnover Cleaning
            </Button>
          </Stack>
        </Box>
      </SectionBlock>
    </PageLayout>
  );
};

export default HospitalityLinenPage;
